import { useState, useEffect, useRef } from "react";
import { MapPin, Plane, TrainFront, Building2, Search, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const ICONS = { airport: Plane, station: TrainFront, city: Building2, town: Building2 };

export function LocationAutocomplete({ value, onSelect, placeholder, testId, icon: Icon = MapPin }) {
  const [query, setQuery] = useState(value?.name || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => { setQuery(value?.name || ""); }, [value]);

  useEffect(() => {
    const handler = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const runSearch = (q) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/locations/search", { params: { q } });
        setResults(data);
        setOpen(true);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 200);
  };

  return (
    <div className="relative" ref={boxRef}>
      <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 h-12 focus-within:border-brand focus-within:ring-2 focus-within:ring-brand/20 transition">
        <Icon size={18} className="text-brand shrink-0" />
        <input
          data-testid={testId}
          value={query}
          placeholder={placeholder}
          onChange={(e) => { setQuery(e.target.value); runSearch(e.target.value); }}
          onFocus={() => runSearch(query)}
          className="w-full bg-transparent outline-none text-sm text-slate-900 placeholder:text-slate-400"
        />
        {loading && <Loader2 size={16} className="animate-spin text-slate-400" />}
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-40 mt-1 w-full rounded-xl border border-slate-200 bg-white shadow-xl max-h-72 overflow-auto no-scrollbar">
          {results.map((r) => {
            const RI = ICONS[r.type] || MapPin;
            return (
              <button
                key={r.id}
                type="button"
                data-testid={`loc-option-${r.id}`}
                onClick={() => { onSelect(r); setQuery(r.name); setOpen(false); }}
                className="flex w-full items-start gap-3 px-3 py-2.5 text-left hover:bg-brand-light transition"
              >
                <RI size={16} className="mt-0.5 text-slate-500 shrink-0" />
                <span>
                  <span className="block text-sm font-medium text-slate-900">{r.name}</span>
                  <span className="block text-xs text-slate-500 capitalize">{r.type} · {r.region} · {r.postcode}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}
      {open && !loading && results.length === 0 && query && (
        <div className="absolute z-40 mt-1 w-full rounded-xl border border-slate-200 bg-white shadow-xl px-3 py-3 text-sm text-slate-500 flex items-center gap-2">
          <Search size={15} /> No matching locations. Try a city, postcode or airport.
        </div>
      )}
    </div>
  );
}
